import { SnackbarProvider } from 'notistack';
import React from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import {
  FlagContextType,
  FlagsContext,
  PortalConfigContext,
  RouteContext,
  SessionData,
} from 'src/context';
import { ElectronTitleBar } from 'src/features/electron';
import { useIsAssemblyBrandEnabled } from 'src/hooks/useIsAssemblyBrandEnabled';
import { initI18nResources } from 'src/i18n/i18next.config';
import { AppProviders } from 'src/legacy/components/App/AppProviders';
import { ConfigureAuth } from 'src/legacy/components/Auth/ConfigureAuth';
import Notifier from 'src/legacy/components/Notifier/Notifier';

interface UnauthAppProps {
  context: {
    insertCss: (...styles: any[]) => () => void;
    pathname: string;
    query: Record<string, any>;
    isMobile: boolean;
    portalConfig: any;
    sessionData: SessionData;
    store: any;
    flags: FlagContextType;
    stripeContext: { publishableKey: string };
    params: Record<string, string>;
  };
  children: React.ReactNode;
}

const UnauthApp: React.FC<UnauthAppProps> = ({ context, children }) => {
  // NOTE: If you need to add or modify header, footer etc. of the app,
  // please do that inside the Layout component.
  const {
    insertCss,
    pathname,
    query,
    isMobile,
    portalConfig,
    sessionData,
    store,
    flags,
    params,
  } = context;

  const { isAssemblyBrandEnabled } = useIsAssemblyBrandEnabled();
  const routeContext = React.useMemo(
    () => ({ pathname, query, isMobile, sessionData, params }),
    [pathname, query, isMobile, sessionData, params],
  );

  // inject the custom labels into the i18n instance
  // and instantiate the i18n resources
  initI18nResources({
    customLabels: portalConfig.features.customLabels,
    isAssembly: isAssemblyBrandEnabled,
  });

  return (
    <FlagsContext.Provider value={flags}>
      <PortalConfigContext.Provider value={portalConfig}>
        <AppProviders insertCss={insertCss}>
          <ReduxProvider store={store}>
            <ElectronTitleBar />
            <RouteContext.Provider value={routeContext}>
              <SnackbarProvider
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                maxSnack={2}
              >
                <Notifier />
                <ConfigureAuth />
                {React.Children.only(children)}
              </SnackbarProvider>
            </RouteContext.Provider>
          </ReduxProvider>
        </AppProviders>
      </PortalConfigContext.Provider>
    </FlagsContext.Provider>
  );
};

export default UnauthApp;
